import { Searchbar } from "@/components/Searchbar"
import { SEARCH_BAR_DEFAULT_MAX_WIDTH } from "@/components/Searchbar/constants"
import { TabList } from "@/components/TabList"
import { H3 } from "@/components/Typographies"
import { CollaboratorRole } from "@/domain/collaborator/constants"
import { SHARED_DATA_TYPE } from "@/domain/sharedData/constants"
import { useCurrentSearchParam } from "@/hooks/useCurrentSearchParam"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import useGetUserRoles from "@/hooks/useGetUserRoles"
import { STALE_TIME_FIVE_MINUTES_IN_MS } from "@/utils/constants"
import { isNotBlank, isNotNullNorUndefined } from "@/utils/ts-utils"
import { Flex, Tab, TabPanel, TabPanels, Tabs } from "@chakra-ui/react"
import { useMemo } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { SharedTable } from "./components/Table/SharedTable"
import { TableLayout } from "./components/TableLayout/TableLayout"
import { useGetInfiniteQuery } from "./components/TableLayout/hook/useGetInfiniteQuery"

export const People: React.FC = () => {
  const intl = useIntl()

  const { data: userInfos, clusterIdsByRole, collaboratorsMyBarnesIds } = useGetUserInfos()
  const { hasRoleGuru } = useGetUserRoles()

  const userId = useMemo(
    () => (hasRoleGuru() ? undefined : userInfos?.id),
    [hasRoleGuru, userInfos]
  )

  const clusterIdsAsManager = useMemo(
    () => clusterIdsByRole[CollaboratorRole.MANAGER] ?? [],
    [clusterIdsByRole]
  )

  const currentSearchTerm = useCurrentSearchParam()

  const {
    query: requests,
    items: requestFromItems,
    fetchNextPage: fetchNextRequestSharedItemsPage,
    hasNextPage: hasNextRequestSharedItemsPage,
    isLoading: isLoadingRequestSharedItemsData,
    // count: requestSharedItemsCount,
  } = useGetInfiniteQuery(
    {
      search: currentSearchTerm,
      "fromCluster.collaborator.collaborator.users.id": userId,
      "type.code": SHARED_DATA_TYPE.PEOPLE,
    },
    {
      enabled: isNotNullNorUndefined(userInfos),
      staleTime: STALE_TIME_FIVE_MINUTES_IN_MS,
    }
  )
  const requestSharedItems = useMemo(
    () =>
      requestFromItems.filter(
        item =>
          clusterIdsAsManager.includes(item.fromCluster?.id ?? "") ||
          collaboratorsMyBarnesIds.includes(item.interBu?.fromManager?.id ?? "") ||
          (isNotBlank(userId) && item.from?.id === userId)
      ),
    [requestFromItems, clusterIdsAsManager, userId, collaboratorsMyBarnesIds]
  )
  // const requestCount = useMemo(() => requestSharedItems.length, [requestSharedItems])

  const {
    query: responses,
    items: responseToItems,
    fetchNextPage: fetchNextResponseSharedItemsPage,
    hasNextPage: hasNextResponseSharedItemsPage,
    isLoading: isLoadingResponseSharedItemsData,
    // count: responseSharedItemsCount,
  } = useGetInfiniteQuery(
    {
      search: currentSearchTerm,
      "toCluster.collaborator.collaborator.users.id": userId,
      "type.code": SHARED_DATA_TYPE.PEOPLE,
    },
    {
      enabled: isNotNullNorUndefined(userInfos),
      staleTime: STALE_TIME_FIVE_MINUTES_IN_MS,
    }
  )
  const responseSharedItems = useMemo(
    () =>
      responseToItems.filter(
        item =>
          clusterIdsAsManager.includes(item.toCluster?.id ?? "") ||
          collaboratorsMyBarnesIds.includes(item.toManager?.id ?? "") ||
          (isNotBlank(userId) && item.to?.id === userId)
      ),
    [responseToItems, clusterIdsAsManager, userId, collaboratorsMyBarnesIds]
  )
  // const responseCount = useMemo(() => responseSharedItems.length, [responseSharedItems])

  const reloadShares = () => {
    requests.refetch()
    responses.refetch()
  }

  return (
    <Flex flexDir="column" gap="6" px="8" py="12">
      <H3>
        <FormattedMessage id="sharing.people.title" />
      </H3>

      <Flex gap="8">
        <Searchbar
          searchbarPlaceholder={intl.formatMessage({
            id: "sharing.search.placeholder",
          })}
          submitButtonAriaLabel={intl.formatMessage({
            id: "sharing.search.button.ariaLabel",
          })}
          maxW={SEARCH_BAR_DEFAULT_MAX_WIDTH}
          flexGrow={1}
        />
      </Flex>

      <Tabs variant="line" isLazy>
        <TabList>
          <Tab>
            <FormattedMessage id="sharing.people.sent" />
            {/* <ListCount count={requestCount} loading={isLoadingRequestSharedItemsData} /> */}
          </Tab>
          <Tab>
            <FormattedMessage id="sharing.people.received" />
            {/* <ListCount count={responseCount} loading={isLoadingResponseSharedItemsData} /> */}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <TableLayout
              currentSearchTerm={currentSearchTerm}
              items={requestSharedItems}
              fetchNextPage={fetchNextRequestSharedItemsPage}
              hasNextPage={hasNextRequestSharedItemsPage}
              isFetchingData={isLoadingRequestSharedItemsData}
            >
              <SharedTable
                items={requestSharedItems}
                type={SHARED_DATA_TYPE.PEOPLE}
                fromOrTo="to"
                refreshQueries={reloadShares}
              />
            </TableLayout>
          </TabPanel>
          <TabPanel>
            <TableLayout
              currentSearchTerm={currentSearchTerm}
              items={responseSharedItems}
              fetchNextPage={fetchNextResponseSharedItemsPage}
              hasNextPage={hasNextResponseSharedItemsPage}
              isFetchingData={isLoadingResponseSharedItemsData}
            >
              <SharedTable
                items={responseSharedItems}
                type={SHARED_DATA_TYPE.PEOPLE}
                fromOrTo="from"
                refreshQueries={reloadShares}
              />
            </TableLayout>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  )
}
